import { useMemo } from "react";
import type { ReactNode } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@b2bportal/core-ui";
import { HotelShopSection } from "@tenant/hotels";
import clsx from "clsx";
import DOMPurify from "dompurify";
import defaultStyles from "./CheckInPoliciesSection.module.scss";

export type CheckInPolicy = {
  title: string;
  text: string | ReactNode;
};

export interface CheckInPoliciesSectionComponentProps
  extends PlatformComponentProps {
  checkInPolicies?: CheckInPolicy[];
}

const COMPONENT_KEY = CoreHotelsComponent.CheckInPoliciesSection;

const cleanPolicy = (policy: string) =>
  policy.split("\n").map((str) => str.trim().replace(/^-\s*/, ""));

const containsHtml = (value: string) => /<[a-zA-Z][\s\S]*?>/.test(value);

// Configure DOMPurify to add rel="noopener noreferrer" to links with target attribute
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const sanitizePolicyHtml = (value: string) =>
  DOMPurify.sanitize(value, { ADD_ATTR: ["target", "rel"] });

const renderPolicyText = (
  text: string,
  cn: (element: string) => string
): ReactNode => {
  const items = cleanPolicy(text);

  if (items.length === 1 && containsHtml(items[0])) {
    return (
      <div
        className={cn("policy-content")}
        dangerouslySetInnerHTML={{ __html: sanitizePolicyHtml(items[0]) }}
      />
    );
  }

  return (
    <ul className={cn("policy-content")}>
      {items.map((item, index) => (
        <li key={index} className={cn("policy-item")}>
          {containsHtml(item) ? (
            <span
              dangerouslySetInnerHTML={{ __html: sanitizePolicyHtml(item) }}
            />
          ) : (
            item
          )}
        </li>
      ))}
    </ul>
  );
};

export const CheckInPoliciesSection = (
  props: CheckInPoliciesSectionComponentProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { children, className, checkInPolicies } = props;

  const content = useMemo(
    () => (
      <Accordion
        type="single"
        className={cn("policy-list")}
        defaultValue="policy-0"
        collapsible
      >
        {checkInPolicies?.map(({ title, text }, index) => {
          //TODO: remove when we have validated BE will never send empty string.
          if (text === "") {
            return null;
          }

          return (
            <AccordionItem
              className={cn("policy")}
              key={title}
              value={`policy-${index}`}
            >
              <AccordionTrigger>
                <h3 className={cn("policy-title")}>{title}</h3>
              </AccordionTrigger>
              <AccordionContent>
                {typeof text === "string" ? renderPolicyText(text, cn) : text}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    ),
    [cn, checkInPolicies]
  );

  if (checkInPolicies == null || checkInPolicies.length === 0) {
    return null;
  }

  return (
    <HotelShopSection className={clsx(block, className)}>
      <div className={cn("content")}> {content} </div>

      {children}
    </HotelShopSection>
  );
};
